/* tslint:disable:cyclomatic-complexity */

import * as React from "react";
import { Animate } from "sb-resp-ui";
import { PulseBetProgressData, PulseBetProgressPeriodData } from "../../data";
import { returnColor, processColorDiff, splitTime, parseTime } from "sb-resp-lib";

export type TrackColors = { start: TrackColor, end: TrackColor };
export type TrackColor = { r: number, g: number, b: number, a: number };

const defaultStartingWidth = 46; // related to the CSS, please be carefull

/* istanbul ignore next */
function trackProgress(duration: number, callback: Function | undefined, animation: any, elements: HTMLElement[], complete: number, remaining: number, start: number, tweenValue?: any) {
    this.iterator = this.totalTime - (this.totalProgress + (duration * complete));
    let d = 1 - Math.ceil((this.iterator / this.totalTime) * 100) / 100;
    let tmp = processColorDiff(this.colors, d);
    let time = parseTime(this.iterator);
    this.point && (
        this.point.innerText = time,
        this.point.style.background = `${returnColor(tmp)}`
    );
    this.trackBg && (
        this.trackBg.style.background = `linear-gradient(to right, ${returnColor(this.colors.start)}, ${returnColor(tmp)})`
    );
    if (typeof callback === "function") {
        callback(animation.value, d, time);
    }
}

/* istanbul ignore next */
function* animation(segments: PulseBetProgressData, t: number, v1: number, v2: number[]) {
    let t1 = t;
    let animations = segments.periods.map((x, i, a) => {
        let d = 0;
        if (v2[i] === 0) {
            t1 -= x.duration;
        }

        if (v1 > v2[i] && v2[i] > 0) {
            d = x.duration - t1;
        }

        if (v1 === v2[i]) {
            d = x.duration;
        }

        return {
            ...x,
            duration: d,
            startTime: x.startTime
        };
    });

    yield { ...animations[0], width: `33.4%` };
    yield { ...animations[1], width: `66.7%` };
    yield { ...animations[2], width: `100%` };
}

export class Track extends React.Component<
    {
        start?: boolean
        colors: TrackColors
        segments: PulseBetProgressData
        callbacks?: {
            begin?: (period: PulseBetProgressPeriodData) => any
            progress?: (period: PulseBetProgressPeriodData, progress: number, time: string) => any
            complete?: (period: PulseBetProgressPeriodData) => any
        }
    }, {
        anim
        start: boolean
        startingWidth: string
    }> {
    iterator: number
    endTime: number[]
    totalTime: number
    startTime: number[]
    colors: TrackColors
    totalProgress: number
    startingWidth!: string
    getAnimation!: Generator
    scale!: HTMLDivElement | null
    point!: HTMLDivElement | null
    track!: HTMLDivElement | null
    trackBg!: HTMLDivElement | null

    /* istanbul ignore next */
    constructor(props) {
        super(props);

        this.colors = this.props.colors;
        this.endTime = splitTime(this.props.segments.endTime);
        this.startTime = splitTime(this.props.segments.startTime);
        // this.totalTime = ((this.endTime[0] * 60 + this.endTime[1]) * 1000) - ((this.startTime[0] * 60 + this.startTime[1]) * 1000)
        this.totalTime = this.props.segments.periods.reduce((a,b) => a += b.duration, 0) * 1000;
        this.totalProgress = this.props.segments.expiredBetProgressInMilliseconds;

        this.iterator = this.totalTime - this.totalProgress;

        this.state = {
            anim: {},
            startingWidth: `${defaultStartingWidth}px`,
            start: typeof this.props.start === "undefined" ? true : this.props.start
        };
    }

    /* istanbul ignore next */
    shouldAnimate() {
        if (this.state.start && this.getAnimation) {
            this.startAnimation(this.getAnimation.next());
        }
    }

    /* istanbul ignore next */
    componentDidMount() {
        let v = this.track && this.track.offsetWidth;
        if (v) {
            let v1 = v / 3;
            let t = this.props.segments.expiredBetProgressInMilliseconds / 1000;
            let t1 = t;
            let v2 = this.props.segments.periods.map(x => {
                let o = v1;
                if (t1 > 0) {
                    o = t1 >= x.duration ? 0 : (v1 / x.duration) * (x.duration - t1);
                }
                t1 -= x.duration;
                return o;
            });
            this.getAnimation = animation(this.props.segments, t, v1, v2);
            v2.filter(x => x === 0).forEach(() => this.getAnimation.next());

            const d = v - v2.reduce((x, y) => x += y, 0);
            // The progress calculations are described here
            // https://confluence.sbtech.com/display/FRON/My+pulse+bet+progress+calculation
            let startingWidth = d < v1 ? (1 - defaultStartingWidth / v1) * d + defaultStartingWidth : d;
            this.setState({ startingWidth: `${startingWidth / v * 100}%` });
        }

        this.shouldAnimate();
    }

    /* istanbul ignore next */
    callCallback(cb, params) { typeof cb === "function" && cb(...params); }

    /*istanbul ignore next */
    componentWillReceiveProps(nextProps) {
        if (this.state.start !== nextProps.start) {
            this.setState({ start: nextProps.start }, () => {
                this.shouldAnimate();
            });
        }
    }

    /* istanbul ignore next */
    startAnimation(animation) {
        if (!animation.done) {

            let duration = animation.value.duration * 1000;

            this.setState({
                anim: {
                    animation: { width: animation.value.width },
                    easing: "linear",
                    duration,
                    begin: () => {
                        if (this.props.callbacks && this.props.callbacks.begin) {
                            this.callCallback(this.props.callbacks.begin, [animation.value])
                        }
                    },
                    progress: (elements: HTMLElement[], complete: number, remaining: number, start: number, tweenValue?: any) => {
                        let cb;
                        if (this.props.callbacks && this.props.callbacks.progress) {
                            cb = this.props.callbacks.progress;
                        }
                        trackProgress.call(this, duration, cb, animation, elements, complete, remaining, start, tweenValue);
                    },
                    complete: () => {
                        this.totalProgress += duration;

                        this.startAnimation(this.getAnimation.next());
                        if (this.props.callbacks && this.props.callbacks.complete) {
                            this.callCallback(this.props.callbacks.complete, [animation.value])
                        }
                    }
                },
            })
        }
    }

    /* istanbul ignore next */
    render() {
        return (
            <div className="rj-pulse-bet__track" ref={n => this.track = n}>
                <Animate {...this.state.anim} runOnMount={true} loop={false} reverse={false}>
                    <div className="rj-pulse-bet__track-wrap" style={{ width: `${this.state.startingWidth}` }}>
                        <div className="rj-pulse-bet__track-bg" ref={n => this.trackBg = n}></div>
                        <div className="rj-pulse-bet__track-point" ref={n => this.point = n}>
                            {parseTime(this.iterator)}
                        </div>
                    </div>
                </Animate>
            </div>
        )
    }
}